import { useEffect, useState } from "react";
import styled from "styled-components";
import { GetInquiryData } from "../Api/callData.js";
import { DataNotFound } from "../Api/dataNotFound.js";

/* ──────────────────────────────────────────────────────────────────────── */
const Page = styled.div`
  background: #ffffff;
  color: #0f172a;
  font-family: "Pretendard Variable", Pretendard, -apple-system,
    BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

const Container = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: 0 24px;
`;

/* ── Hero ── */
const Hero = styled.section`
  margin-top: calc(-1 * clamp(60px, 10vw, 120px));
  padding: calc(clamp(96px, 12vw, 160px) + clamp(60px, 10vw, 120px)) 0
    clamp(48px, 6vw, 72px);
  text-align: center;
  background:
    radial-gradient(ellipse 70% 50% at 50% 0%, #eef2ff 0%, transparent 70%),
    #ffffff;
`;

const Eyebrow = styled.p`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #6366f1;
  margin: 0 0 16px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(40px, 5.8vw, 76px);
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.035em;
  color: #0f172a;
  margin: 0 0 20px;
`;

const HeroLead = styled.p`
  font-size: clamp(16px, 1.5vw, 19px);
  line-height: 1.65;
  color: #64748b;
  margin: 0 auto 36px;
  max-width: 580px;
`;

const Tabs = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
`;

const Tab = styled.a`
  padding: 9px 22px;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 500;
  text-decoration: none;
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "transparent"};
  box-shadow: ${(p) =>
    p.$active ? "0 6px 14px -6px rgba(99, 102, 241, 0.55)" : "none"};
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
`;

/* ── Content ── */
const ContentSection = styled.section`
  padding: clamp(32px, 4vw, 48px) 0 clamp(80px, 10vw, 120px);
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;

  .count {
    font-size: 13.5px;
    color: #94a3b8;
  }
  .count strong {
    color: #0f172a;
    font-weight: 600;
  }
`;

const WriteBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 999px;
  background: #0f172a;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: #1e293b;
  }
`;

const List = styled.div`
  border-top: 1px solid #e2e8f0;
`;

const Row = styled.a`
  display: grid;
  grid-template-columns: 64px 110px 1fr 120px 110px;
  align-items: center;
  gap: 16px;
  padding: 18px 12px;
  border-bottom: 1px solid #e2e8f0;
  text-decoration: none;
  color: inherit;
  transition: background 0.15s ease;

  &:hover {
    background: #f8fafc;
  }

  .index {
    font-size: 13px;
    color: #94a3b8;
    text-align: center;
  }

  .status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    width: fit-content;
  }
  .status.waiting {
    background: #fffbeb;
    color: #d97706;
    border: 1px solid #fde68a;
  }
  .status.complete {
    background: #ecfdf5;
    color: #10b981;
    border: 1px solid #a7f3d0;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    color: #0f172a;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    .lock {
      color: #94a3b8;
      flex-shrink: 0;
    }
  }

  .writer {
    font-size: 13.5px;
    color: #475569;
  }

  .date {
    font-size: 13px;
    color: #94a3b8;
    text-align: right;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 18px 4px;

    .index,
    .writer {
      display: none;
    }
    .date {
      text-align: left;
    }
  }
`;

/* ── Pagination ── */
const Pagination = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 48px;
`;

const PageBtn = styled.button`
  min-width: 36px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid ${(p) => (p.$active ? "transparent" : "#e2e8f0")};
  border-radius: 8px;
  background: ${(p) =>
    p.$active
      ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
      : "#ffffff"};
  color: ${(p) => (p.$active ? "#ffffff" : "#475569")};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  transition: border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${(p) => (p.$active ? "transparent" : "#cbd5e1")};
    color: ${(p) => (p.$active ? "#ffffff" : "#0f172a")};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

/* ── States ── */
const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 24px;
  text-align: center;
  color: #94a3b8;
  font-size: 15px;
`;

const Spinner = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

/* ── Icons ── */
const LockIcon = () => (
  <svg className="lock" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 18l-6-6 6-6" />
  </svg>
);
const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 18l6-6-6-6" />
  </svg>
);
const ChevronsLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 18l-6-6 6-6M19 18l-6-6 6-6" />
  </svg>
);
const ChevronsRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M13 18l6-6-6-6M5 18l6-6-6-6" />
  </svg>
);

const PAGE_SIZE = 10;

export function Inquiry() {
  const [post, setPost] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [nowPath, setNowPath] = useState("");

  useEffect(() => {
    const lastSegment = window.location.pathname.split("/").pop();
    setNowPath(lastSegment);
  }, []);

  const loadInquiry = async (page) => {
    setLoading(true);
    const { postsData, lastVisibleDoc, totalSize } = await GetInquiryData(
      page,
      lastVisible
    );
    setPost(postsData);
    setLastVisible(lastVisibleDoc);
    setLoading(false);
    setTotalPosts(totalSize);
  };

  useEffect(() => {
    loadInquiry(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const totalPages = Math.max(1, Math.ceil((totalPosts || 0) / PAGE_SIZE));

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <Page>
      <Hero>
        <Container>
          <Eyebrow>Support</Eyebrow>
          <HeroTitle>Inquiry</HeroTitle>
          <HeroLead>
            Get in touch with our team — we respond to every question.
          </HeroLead>
          <Tabs>
            <Tab href="/support/faq">FAQ</Tab>
            <Tab href="/support/inquiry" $active>
              Inquiry
            </Tab>
          </Tabs>
        </Container>
      </Hero>

      <ContentSection>
        <Container>
          {loading ? (
            <StateBox>
              <Spinner />
              <div>Loading…</div>
            </StateBox>
          ) : post && post.length > 0 ? (
            <>
              <TopBar>
                <div className="count">
                  Total <strong>{totalPosts}</strong>
                </div>
                <WriteBtn href="/support/inquiry/inquiryWrite">
                  Write inquiry
                </WriteBtn>
              </TopBar>

              <List>
                {post.map((p) => (
                  <Row key={p.id} href={`/support/inquiry/${p.id}`}>
                    <span className="index">{p.overallIndex}</span>
                    <span
                      className={`status ${
                        p.hasComments ? "complete" : "waiting"
                      }`}
                    >
                      {p.hasComments ? "Complete" : "Waiting"}
                    </span>
                    <span className="title">
                      {p.secret && <LockIcon />}
                      <span>{p.title}</span>
                    </span>
                    <span className="writer">{p.userName || "Guest"}</span>
                    <span className="date">
                      {p.reg_time.toDate().toISOString().split("T")[0]}
                    </span>
                  </Row>
                ))}
              </List>

              <Pagination aria-label="Pagination">
                <PageBtn
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="First page"
                >
                  <ChevronsLeftIcon />
                </PageBtn>
                <PageBtn
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon />
                </PageBtn>
                {[...Array(totalPages)].map((_, i) => (
                  <PageBtn
                    key={i}
                    $active={currentPage === i + 1}
                    onClick={() => handlePageChange(i + 1)}
                  >
                    {i + 1}
                  </PageBtn>
                ))}
                <PageBtn
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Next page"
                >
                  <ChevronRightIcon />
                </PageBtn>
                <PageBtn
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Last page"
                >
                  <ChevronsRightIcon />
                </PageBtn>
              </Pagination>
            </>
          ) : (
            <>
              <TopBar>
                <div className="count">
                  Total <strong>0</strong>
                </div>
                <WriteBtn href="/support/inquiry/inquiryWrite">
                  Write inquiry
                </WriteBtn>
              </TopBar>
              <DataNotFound table={nowPath} />
            </>
          )}
        </Container>
      </ContentSection>
    </Page>
  );
}
